import interpolateComponents from '@automattic/interpolate-components';
import { useMemo } from 'react';

import type { ApiAddress } from 'api/types/ApiAddress';
import type { ApiListingLocationType } from 'api/types/ApiListingLocationType';
import type { ApiRemoteOptions } from 'api/types/ApiRemoteOptions';
import { Address } from 'components/Address/Address';
import { Box } from 'components/Box/Box';
import { Icon } from 'components/Icon/Icon';
import type { IconName } from 'components/Icon/IconName';
import { LegacyTooltip } from 'components/LegacyTooltip/LegacyTooltip';
import { AddressLink } from 'components/Link/AddressLink';
import { HeadingSmall } from 'components/Text/HeadingSmall';
import { ListingViewSection } from 'modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingRichText } from 'modules/listing/components/RichText/ListingRichText';
import type { Color } from 'theme/theme';
import { colors, fontWeights } from 'theme/theme';
import { getWorkLocationSentence } from 'utils/address/getWorkLocationSentence';
import { trackAnchorClicked } from 'utils/analytics/track/trackAnchorClicked';
import { getLocationTypeText } from 'utils/listings/getLocationTypeText';

const iconDataByLocationType = {
  ONSITE: { icon: 'building', color: 'brandGreen' },
  REMOTE: { icon: 'house', color: 'brightPlum' },
  HYBRID: { icon: 'hybrid-location', color: 'skyBlue' },
} satisfies Record<ApiListingLocationType, { icon: IconName; color: Color }>;

type Props = {
  address: ApiAddress;
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  directions?: string | null;
  linkToMap?: boolean;
  remoteOptions: ApiRemoteOptions;
};

export function ListingViewLocationSection({
  address,
  directions,
  listingType,
  linkToMap,
  remoteOptions,
}: Props) {
  const addressSection =
    address &&
    (linkToMap ? (
      <AddressLink
        address={address}
        onClick={(event) =>
          trackAnchorClicked(event, 'Clicked: Listing Address', {
            type: listingType,
          })
        }
      />
    ) : (
      <Address address={address} />
    ));

  const locationSentence = useMemo(
    () =>
      getWorkLocationSentence({
        listingType,
        remoteOptions,
        address,
      }),
    [address, listingType, remoteOptions],
  );

  return (
    <ListingViewSection
      title={
        /* TRANSLATORS: Listing Page, section title */
        getText('Location')
      }
    >
      {remoteOptions ? (
        <div>
          <Box
            position="relative"
            display="inline-flex"
            pr={12}
            pl={40}
            py="6px"
            mb={24}
            overflow="hidden"
            boxShadow={`0 0 0 1px inset ${
              colors[iconDataByLocationType[remoteOptions.locationType].color]
            }`}
            borderRadius="3px"
            textTransform="uppercase"
            fontWeight={fontWeights.semiBold}
            fontSize={14}
            letterSpacing="1px"
          >
            <Box
              position="absolute"
              left={0}
              top={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              width={30}
              height={32}
              bg={
                colors[iconDataByLocationType[remoteOptions.locationType].color]
              }
            >
              <Icon
                name={iconDataByLocationType[remoteOptions.locationType].icon}
                size={20}
                colorName="white"
              />
            </Box>
            {getLocationTypeText(listingType, remoteOptions.locationType)}
          </Box>

          {remoteOptions.locationType !== 'ONSITE' && locationSentence ? (
            <div>
              {interpolateComponents({
                mixedString: locationSentence,
                components: { strong: <strong /> },
              })}
            </div>
          ) : null}

          {remoteOptions.locationType === 'REMOTE' ||
          remoteOptions.locationType === 'HYBRID' ? (
            <>
              <HeadingSmall mb={0} mt={24}>
                <Box display="flex" alignItems="center">
                  {getText('Associated Location')}

                  <div style={{ marginLeft: 4 }}>
                    <LegacyTooltip>
                      {remoteOptions.locationType === 'HYBRID' &&
                        getText(
                          'An associated location for a hybrid listing may be the headquarters of an organization or other related location.',
                        )}
                      {remoteOptions.locationType === 'REMOTE' &&
                        getText(
                          `An associated location for a remote listing may be the headquarters of an organization or other related location, but this is still a telecommute opportunity.`,
                        )}
                    </LegacyTooltip>
                  </div>
                </Box>
              </HeadingSmall>

              <div style={{ color: colors.lightContentGrey, marginTop: 8 }}>
                {addressSection}
              </div>
            </>
          ) : (
            remoteOptions.locationType === 'ONSITE' && addressSection
          )}
        </div>
      ) : (
        addressSection
      )}

      {directions && (
        <div style={{ marginTop: 24, color: colors.lightContentGrey }}>
          <HeadingSmall mb={0}>{getText('Directions')}</HeadingSmall>
          <ListingRichText content={directions} />
        </div>
      )}
    </ListingViewSection>
  );
}
